import { useEffect, useRef, useState } from "react"

// Bottom-anchored modal sheet — the mobile presentation for the Pro upsell and
// the save-to-inbox prompt.
//
// Native <dialog> rather than a hand-rolled overlay, matching HeroResult's
// capture modal and PricingModal: it brings Esc, a real focus trap, focus
// restore on close, and top-layer stacking that can't be defeated by a parent
// stacking context. The only thing overridden is placement — `m-auto mb-0`
// pins it to the bottom of the viewport instead of centring it.
//
// The scrim is a labelled button, not a bare div, so a screen reader user has
// the same dismissal affordance a pointer user does.
export default function MobileSheetModal({ open, onClose, label, children }) {
  const dialogRef = useRef(null)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    if (!open) return
    const dialog = dialogRef.current
    if (dialog && !dialog.open) dialog.showModal()
    // One frame before adding the transform, so the rise-in actually animates
    // instead of being collapsed into the initial paint.
    const raf = requestAnimationFrame(() => setShown(true))
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    const handleCancel = (e) => { e.preventDefault(); onClose() } // Esc
    dialog?.addEventListener("cancel", handleCancel)
    return () => {
      cancelAnimationFrame(raf)
      document.body.style.overflow = prevOverflow
      dialog?.removeEventListener("cancel", handleCancel)
      setShown(false)
    }
  }, [ open, onClose ])

  if (!open) return null

  return (
    <dialog
      ref={dialogRef}
      aria-label={label}
      className="m-auto mb-0 w-full max-w-none max-h-[92dvh] p-0 bg-transparent backdrop:bg-coal/50 backdrop:backdrop-blur-[2px]"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="fixed inset-0 w-full h-full cursor-pointer border-0 bg-transparent p-0"
      />
      <div
        className={
          "relative flex w-full flex-col gap-2.5 rounded-t-[24px] px-5 pt-2.5 " +
          "bg-[color:var(--bg-surface)] text-[color:var(--text-primary)] " +
          "shadow-[0_-14px_40px_rgba(17,17,17,0.22)] " +
          "transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] " +
          (shown ? "translate-y-0 opacity-100" : "translate-y-3.5 opacity-0")
        }
        // The home indicator sits under the card on a modern iPhone; 30px is
        // the design's padding on a device without one.
        style={{ paddingBottom: "calc(30px + env(safe-area-inset-bottom))" }}
      >
        <span aria-hidden="true" className="mb-1.5 h-1 w-[38px] self-center rounded-sm bg-[color:var(--border-default)]" />
        {children}
      </div>
    </dialog>
  )
}
